import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Plus, Boxes, Filter, Search, X, Calendar, DollarSign, History } from 'lucide-react';
import { InventoryLogs } from '../components/InventoryLogs';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const statusVariants = {
    AVAILABLE: 'success',
    ORDERED: 'warning',
    PENDING_DELIVERY: 'info',
    SOLD: 'default',
    RETURNED: 'purple',
    DAMAGED: 'danger'
};

export function InventoryPage() {
    const { t, language } = useLanguage();
    const [units, setUnits] = useState([]);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('stock');

    // Filter states
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [productFilter, setProductFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');

    const [formData, setFormData] = useState({
        productId: '',
        buyPrice: '',
        quantity: 1,
        notes: ''
    });

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    // Debounced search
    const [debouncedSearch, setDebouncedSearch] = useState('');
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        fetchData();
    }, [statusFilter, productFilter, categoryFilter, startDate, endDate, minPrice, maxPrice, debouncedSearch]);

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const res = await axios.get(`${API_URL}/categories`, { headers });
            setCategories(res.data.data.categories || []);
        } catch (error) {
            console.error('Failed to fetch categories:', error);
        }
    };

    const fetchData = async () => {
        try {
            const params = new URLSearchParams();
            if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
            if (productFilter && productFilter !== 'all') params.append('productId', productFilter);
            if (categoryFilter && categoryFilter !== 'all') params.append('categoryId', categoryFilter);
            if (debouncedSearch) params.append('search', debouncedSearch);
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            if (minPrice) params.append('minPrice', minPrice);
            if (maxPrice) params.append('maxPrice', maxPrice);

            const [inventoryRes, productsRes] = await Promise.all([
                axios.get(`${API_URL}/inventory?${params}`, { headers }),
                axios.get(`${API_URL}/products`, { headers })
            ]);
            setUnits(inventoryRes.data.data.units);
            setProducts(productsRes.data.data.products);
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_URL}/inventory`, formData, { headers });
            setDialogOpen(false);
            setFormData({ productId: '', buyPrice: '', quantity: 1, notes: '' });
            fetchData();
        } catch (error) {
            console.error('Failed to add units:', error);
        }
    };

    const handleStatusChange = async (unitId, newStatus) => {
        try {
            await axios.patch(`${API_URL}/inventory/${unitId}/status`, { status: newStatus }, { headers });
            fetchData();
        } catch (error) {
            console.error('Failed to update status:', error);
        }
    };

    const clearFilters = () => {
        setSearch('');
        setStatusFilter('all');
        setProductFilter('all');
        setCategoryFilter('all');
        setStartDate('');
        setEndDate('');
        setMinPrice('');
        setMaxPrice('');
    };

    const hasActiveFilters = search || statusFilter !== 'all' || productFilter !== 'all' ||
        categoryFilter !== 'all' || startDate || endDate || minPrice || maxPrice;

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US');
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold">{t('inventory')}</h1>
                        <p className="text-muted-foreground">{t('inventorySubtitle')}</p>
                    </div>
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button>
                                <Plus className="h-4 w-4 mr-2" />
                                {t('addUnits')}
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <form onSubmit={handleSubmit}>
                                <DialogHeader>
                                    <DialogTitle>{t('addInventoryUnits')}</DialogTitle>
                                    <DialogDescription>
                                        {t('addInventorySubtitle')}
                                    </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4 py-4">
                                    <div>
                                        <Label htmlFor="product">{t('productName')}</Label>
                                        <Select
                                            value={formData.productId}
                                            onValueChange={(value) => setFormData({ ...formData, productId: value })}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder={t('selectProduct')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {products.map((product) => (
                                                    <SelectItem key={product.id} value={product.id}>
                                                        {product.name} ({product.sku})
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label htmlFor="buyPrice">{t('buyPrice')} ({t('costBasis')})</Label>
                                        <Input
                                            id="buyPrice"
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={formData.buyPrice}
                                            onChange={(e) => setFormData({ ...formData, buyPrice: e.target.value })}
                                            placeholder="0.00"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="quantity">{t('quantity')}</Label>
                                        <Input
                                            id="quantity"
                                            type="number"
                                            min="1"
                                            max="100"
                                            value={formData.quantity}
                                            onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="notes">{t('notes')} (Optional)</Label>
                                        <Input
                                            id="notes"
                                            value={formData.notes}
                                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                            placeholder={t('notesPlaceholder')}
                                        />
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button type="submit">{t('addUnits')}</Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Tabs Navigation */}
                <div className="flex space-x-4 border-b pb-2 mb-4">
                    <button
                        className={`pb-2 px-1 flex items-center gap-2 ${activeTab === 'stock' ? 'border-b-2 border-primary font-bold text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                        onClick={() => setActiveTab('stock')}
                    >
                        <Boxes className="h-4 w-4" />
                        {t('currentStock') || 'Current Stock'}
                    </button>
                    <button
                        className={`pb-2 px-1 flex items-center gap-2 ${activeTab === 'logs' ? 'border-b-2 border-primary font-bold text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                        onClick={() => setActiveTab('logs')}
                    >
                        <History className="h-4 w-4" />
                        {t('operationsLog') || 'Operations Log'}
                    </button>
                </div>

                {activeTab === 'stock' && (
                    <>
                        {/* Filters Card */}
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base flex items-center gap-2">
                                    <Filter className="h-4 w-4" />
                                    {t('filters')}
                                    {hasActiveFilters && (
                                        <Badge variant="secondary" className="ml-2">
                                            {t('active')}
                                        </Badge>
                                    )}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {/* Search Row */}
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder={t('searchInventory')}
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="pl-9"
                                    />
                                </div>

                                {/* Dropdowns Row */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                                        <SelectTrigger>
                                            <SelectValue placeholder={t('allStatuses')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">{t('allStatuses')}</SelectItem>
                                            {Object.keys(statusVariants).map((status) => (
                                                <SelectItem key={status} value={status}>{t(status === 'PENDING_DELIVERY' ? 'pendingDelivery' : status.toLowerCase())}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Select value={productFilter} onValueChange={setProductFilter}>
                                        <SelectTrigger>
                                            <SelectValue placeholder={t('allProducts')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">{t('allProducts')}</SelectItem>
                                            {products.map((product) => (
                                                <SelectItem key={product.id} value={product.id}>{product.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                                        <SelectTrigger>
                                            <SelectValue placeholder={t('allCategories')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">{t('allCategories')}</SelectItem>
                                            {categories.map((cat) => (
                                                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Date & Price Range Row */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="space-y-1">
                                        <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                            <Calendar className="h-3 w-3" /> {t('dateFrom')}
                                        </Label>
                                        <Input
                                            type="date"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                            <Calendar className="h-3 w-3" /> {t('dateTo')}
                                        </Label>
                                        <Input
                                            type="date"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                            <DollarSign className="h-3 w-3" /> {t('minPrice')}
                                        </Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            placeholder="0.00"
                                            value={minPrice}
                                            onChange={(e) => setMinPrice(e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                            <DollarSign className="h-3 w-3" /> {t('maxPrice')}
                                        </Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            placeholder="0.00"
                                            value={maxPrice}
                                            onChange={(e) => setMaxPrice(e.target.value)}
                                        />
                                    </div>
                                </div>

                                {/* Clear Filters */}
                                {hasActiveFilters && (
                                    <Button variant="outline" size="sm" onClick={clearFilters} className="w-full md:w-auto">
                                        <X className="h-4 w-4 mr-2" />
                                        {t('clearFilters')}
                                    </Button>
                                )}
                            </CardContent>
                        </Card>

                        {/* Inventory Table */}
                        <div className="border rounded-lg">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>{t('unitUuid')}</TableHead>
                                        <TableHead>{t('productName')}</TableHead>
                                        <TableHead>{t('buyPrice')}</TableHead>
                                        <TableHead>{t('status')}</TableHead>
                                        <TableHead>{t('created')}</TableHead>
                                        <TableHead className="text-right">{t('actions')}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {units.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                                <Boxes className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                                {t('noUnits')}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        units.map((unit) => (
                                            <TableRow key={unit.id}>
                                                <TableCell className="font-mono text-xs">{unit.unitUuid}</TableCell>
                                                <TableCell>
                                                    <div>
                                                        <div className="font-medium">{unit.product?.name}</div>
                                                        <div className="text-xs text-muted-foreground">{unit.product?.sku}</div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>${parseFloat(unit.buyPrice).toFixed(2)}</TableCell>
                                                <TableCell>
                                                    <Badge variant={statusVariants[unit.status]}>
                                                        {t(unit.status === 'PENDING_DELIVERY' ? 'pendingDelivery' : unit.status.toLowerCase())}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-sm text-muted-foreground">
                                                    {formatDate(unit.createdAt)}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Select
                                                        value={unit.status}
                                                        onValueChange={(value) => handleStatusChange(unit.id, value)}
                                                    >
                                                        <SelectTrigger className="w-40">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {Object.keys(statusVariants).map((status) => (
                                                                <SelectItem key={status} value={status}>{t(status === 'PENDING_DELIVERY' ? 'pendingDelivery' : status.toLowerCase())}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </>
                )}

                {activeTab === 'logs' && (
                    <InventoryLogs products={products} categories={categories} />
                )}
            </div>
        </Layout>
    );
}
